import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { fetchWebBranches, fetchBranchServices } from '../api';
import '../styles/pages/Services.scss';

/** Section order + headings for the three offering kinds. */
const SECTIONS = [
    { kind: 'package', title: 'Packages', subtitle: 'membership packages' },
    { kind: 'bundle', title: 'Bundles', subtitle: 'combined memberships' },
    { kind: 'single', title: 'Single Services', subtitle: 'one-off sessions & experiences' },
];

const formatAmount = (amount) => new Intl.NumberFormat('en-KE').format(amount);

const Services = () => {
    usePageTitle('Services');
    const [branches, setBranches] = useState([]);
    const [branchesLoaded, setBranchesLoaded] = useState(false);
    const [branchId, setBranchId] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Branch directory — drives the branch picker.
    useEffect(() => {
        let cancelled = false;
        fetchWebBranches()
            .then((list) => {
                if (cancelled) return;
                setBranches(list);
                if (list.length > 0) setBranchId(list[0].id);
                setBranchesLoaded(true);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message);
                setBranchesLoaded(true);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    // Per-branch services — refetch whenever the selection changes.
    useEffect(() => {
        if (!branchId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        fetchBranchServices(branchId)
            .then((payload) => {
                if (cancelled) return;
                setData(payload);
                setLoading(false);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err.message);
                setLoading(false);
            });
        return () => { cancelled = true; };
    }, [branchId]);

    const heroImage = data && data.items
        ? (data.items.find((i) => i.image_url) || {}).image_url || null
        : null;

    const renderCards = (items) => (
        <div className="row services-grid">
            {items.map((item) => (
                <div className="col-12 col-md-6 col-lg-4" key={`${item.kind}-${item.id}`}>
                    <div className="service-card">
                        <div className="service-card-img">
                            {item.image_url ? (
                                <img src={item.image_url} alt={item.name} />
                            ) : (
                                <div className="service-card-img-empty" />
                            )}
                        </div>
                        <div className="service-card-body">
                            <h4>{item.name}</h4>
                            {item.description && <p>{item.description}</p>}
                            {item.prices && item.prices.length > 0 && (
                                <ul className="service-card-prices">
                                    {item.prices.map((price, i) => (
                                        <li key={i}>
                                            <span className="price-label">{price.label}</span>
                                            <span className="price-amount">
                                                {price.currency} {formatAmount(price.amount)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    return (
        <>
            <Navbar />
            <div className="page-services">
                <section className="services-hero">
                    {heroImage && (
                        <div className="services-hero-bg">
                            <img src={heroImage} alt="Services" />
                            <div className="services-hero-overlay"></div>
                        </div>
                    )}
                    <div className="container">
                        <div className="row">
                            <div className="col-lg-1"></div>
                            <div className="col-12 col-lg-8 col-xl-7">
                                <h1 className="text-white mb-0">Our Services &amp; Experiences</h1>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="services-branch-bar">
                    <div className="container">
                        <div className="services-branch-bar-inner">
                            <label htmlFor="branch-select">Select your branch</label>
                            {branchesLoaded && branches.length === 0 ? (
                                <span className="text-muted">No branches available yet.</span>
                            ) : (
                                <select
                                    id="branch-select"
                                    className="branch-select"
                                    value={branchId}
                                    onChange={(e) => setBranchId(e.target.value)}
                                    disabled={!branchesLoaded}
                                >
                                    {!branchesLoaded && <option>Loading branches…</option>}
                                    {branches.map((branch) => (
                                        <option key={branch.id} value={branch.id}>
                                            {branch.name}
                                            {branch.city ? ` — ${branch.city}` : ''}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                </section>

                <section className="services-list">
                    <div className="container">
                        {loading && (
                            <div className="text-center py-5">
                                <p className="text-muted">Loading services…</p>
                            </div>
                        )}
                        {!loading && error && (
                            <div className="text-center py-5">
                                <p className="text-danger">Unable to load services. Please try again later.</p>
                            </div>
                        )}
                        {!loading && !error && data && data.items.length === 0 && (
                            <div className="text-center py-5">
                                <p className="text-muted">
                                    No services listed for {data.branch_name} yet — check back soon.
                                </p>
                            </div>
                        )}
                        {!loading && !error && data && SECTIONS.map((section) => {
                            const items = data.items.filter((i) => i.kind === section.kind);
                            if (items.length === 0) return null;
                            return (
                                <div className="services-section" key={section.kind}>
                                    <div className="services-section-head">
                                        <p className="subtitle">{section.subtitle}</p>
                                        <h3>{section.title}</h3>
                                    </div>
                                    {renderCards(items)}
                                </div>
                            );
                        })}
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default Services;
